import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const AssessmentPage = ({ auth }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/assessment/questions`);
      // Randomize question order to prevent bias
      const shuffled = [...response.data.questions].sort(() => Math.random() - 0.5);
      setQuestions(shuffled);
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (score) => {
    const question = questions[currentIndex];
    setAnswers({
      ...answers,
      [question.id]: { question_id: question.id, dimension: question.dimension, score: parseInt(score) }
    });
  };

  const handleNext = () => {
    if (!answers[questions[currentIndex].id]) {
      toast.error('Please select an answer');
      return;
    }
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length !== questions.length) {
      toast.error('Please answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/assessment/submit`, {
        user_id: auth.user.id,
        answers: Object.values(answers)
      });
      toast.success('Assessment completed! Check your email for results summary.');
      navigate('/results');
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Insufficient credits. Please purchase an assessment to continue.');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        toast.error('Failed to submit assessment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading assessment...</div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const currentAnswer = answers[currentQuestion?.id]?.score || '';

  const dimensionColors = {
    B: 'bg-purple-100 text-purple-700',
    U: 'bg-blue-100 text-blue-700',
    I: 'bg-green-100 text-green-700',
    L: 'bg-pink-100 text-pink-700',
    D: 'bg-orange-100 text-orange-700',
    R: 'bg-red-100 text-red-700'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="container mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold mb-4 text-gray-800">BUILDR Assessment</h1>
          <p className="text-gray-600 mb-4">
            Question {currentIndex + 1} of {questions.length}
          </p>
          <Progress value={progress} className="h-3" data-testid="assessment-progress" />
        </div>

        {/* Question Card */}
        <Card className="mb-8 shadow-xl fade-in" data-testid="question-card">
          <CardHeader>
            <CardTitle className="text-2xl">{currentQuestion.text}</CardTitle>
            <CardDescription className="text-base mt-2">
              Rate how much this statement describes you
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup 
              value={currentAnswer.toString()} 
              onValueChange={handleAnswer}
              data-testid="answer-options"
            >
              <div className="space-y-4">
                {[
                  { value: '5', label: 'Strongly Agree' },
                  { value: '4', label: 'Agree' },
                  { value: '3', label: 'Neutral' },
                  { value: '2', label: 'Disagree' },
                  { value: '1', label: 'Strongly Disagree' }
                ].map((option) => (
                  <div key={option.value} className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-indigo-50 transition-colors cursor-pointer">
                    <RadioGroupItem value={option.value} id={option.value} data-testid={`option-${option.value}`} />
                    <Label htmlFor={option.value} className="cursor-pointer text-lg flex-1">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            data-testid="previous-btn"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            variant="outline"
            size="lg"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Previous
          </Button>

          {currentIndex === questions.length - 1 ? (
            <Button
              data-testid="submit-assessment-btn"
              onClick={handleSubmit}
              disabled={submitting || Object.keys(answers).length !== questions.length}
              size="lg"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {submitting ? 'Submitting...' : 'Submit Assessment'}
            </Button>
          ) : (
            <Button
              data-testid="next-btn"
              onClick={handleNext}
              size="lg"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Next
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          )}
        </div>

        {/* Progress Summary */}
        <div className="mt-8 text-center text-sm text-gray-500">
          {Object.keys(answers).length} of {questions.length} questions answered
        </div>
      </div>
    </div>
  );
};

export default AssessmentPage;
